/* eslint-disable better-mutation/no-mutation */
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import { getInitialState } from './initial-state';
import type { InitData } from 'tradera/state/init-data/types';

// The max size of the item cache
const RECENTLY_VIEWED_MAX_LENGTH = 20;

type ItemsState = {
    recentItemIds: number[];
    wishListItemIds: number[];
    wishListAddedCount: number;
};

export const initialState: ItemsState = {
    recentItemIds: [],
    wishListItemIds: [],
    wishListAddedCount: 0
};

const itemsSlice = createSlice({
    name: 'items',
    initialState,
    reducers: {
        addRecentlyViewed: (state, action) => {
            const { payload: itemId } = action;
            // Remove item if already present
            state.recentItemIds = state.recentItemIds.filter(
                (x) => x !== itemId
            );
            // Add to front of list
            state.recentItemIds.unshift(itemId);
            // Limit list length
            state.recentItemIds = state.recentItemIds.slice(
                0,
                RECENTLY_VIEWED_MAX_LENGTH
            );
        },
        setRecentlyViewed: (state, action) => {
            const { payload } = action;
            state.recentItemIds = payload.slice(0, RECENTLY_VIEWED_MAX_LENGTH);
        },
        /**
         *
         * @param state
         * @param action { payload: { itemId: number; exists: boolean } } where exists is true if the item should be added to the wishlist, false if it should be removed
         */
        wishlist: (
            state,
            action: PayloadAction<{ itemId: number; exists: boolean }>
        ) => {
            const {
                payload: { itemId, exists }
            } = action;

            const wishListItemIds = new Set(state.wishListItemIds || []);
            if (exists) {
                wishListItemIds.add(itemId);
            } else {
                wishListItemIds.delete(itemId);
            }
            state.wishListItemIds = Array.from(wishListItemIds);
        },
        wishlistCountIncrement: (state) => {
            state.wishListAddedCount = state.wishListAddedCount + 1;
        },
        wishlistAddRange: (state, action: PayloadAction<number[]>) => {
            const { payload: itemIds } = action;
            const wishListItemIds = new Set(state.wishListItemIds || []);
            itemIds?.forEach((itemId) => wishListItemIds.add(itemId));
            state.wishListItemIds = Array.from(wishListItemIds);
        },
        wishlistRemoveRange: (state, action: PayloadAction<number[]>) => {
            const { payload: itemIds } = action;
            const wishListItemIds = new Set(state.wishListItemIds || []);
            itemIds?.forEach((itemId) => wishListItemIds.delete(itemId));
            state.wishListItemIds = Array.from(wishListItemIds);
        },
        initialize: (state, action: PayloadAction<InitData>) => {
            state = getInitialState(action.payload);
        }
    }
});

export const {
    addRecentlyViewed,
    setRecentlyViewed,
    wishlist,
    wishlistAddRange,
    wishlistRemoveRange,
    initialize,
    wishlistCountIncrement
} = itemsSlice.actions;

export default itemsSlice.reducer;
